import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Typography, Container } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { addToCart } from '../actions/actions';
import FoodDetails from './FoodDetails';
import './foodlist.scss';

const FoodList = ({ addToCart }) => {
  const [merchantsData, setMerchantsData] = useState([]);
  const [selectedFood, setSelectedFood] = useState(null);
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem('foodSearchTerm') || ''
  );
  const [selectedMerchant, setSelectedMerchant] = useState(
    localStorage.getItem('selectedMerchantFilter') || 'all'
  );

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const merchantId = localStorage.getItem('merchantId');
    if (!merchantId) {
      console.error('Merchant ID not found in local storage');
      return;
    }

    fetch(`${apiUrl}/foods/mfoods`, {
      headers: { merchantId },
    })
      .then((response) => response.json())
      .then((data) => {
        const safeData = Array.isArray(data) ? data : [];

        const sortedData = safeData
          .map((item) => ({
            ...item,
            foods: [...(item.foods || [])].sort((a, b) =>
              (a.title || '').localeCompare(b.title || '')
            ),
          }))
          .sort((a, b) =>
            (a?.merchant?.name || a?.merchant?.username || '').localeCompare(
              b?.merchant?.name || b?.merchant?.username || ''
            )
          );

        setMerchantsData(sortedData);
      })
      .catch((error) => console.error('Error fetching data:', error));
  }, [apiUrl]);

  useEffect(() => {
    const handleMerchantFilterChanged = (event) => {
      const value = event.detail || 'all';
      setSelectedMerchant(value);
      localStorage.setItem('selectedMerchantFilter', value);
    };

    const handleFoodSearchChanged = (event) => {
      const value = event.detail || '';
      setSearchTerm(value);
      localStorage.setItem('foodSearchTerm', value);
    };

    window.addEventListener('merchantFilterChanged', handleMerchantFilterChanged);
    window.addEventListener('foodSearchChanged', handleFoodSearchChanged);

    return () => {
      window.removeEventListener('merchantFilterChanged', handleMerchantFilterChanged);
      window.removeEventListener('foodSearchChanged', handleFoodSearchChanged);
    };
  }, []);

  const handleViewClick = (food, merchantId) => {
    setSelectedFood({ ...food, merchantId });
  };

  const handleCloseDetails = () => {
    setSelectedFood(null);
  };

  const isCurrentDuration = (startTime, endTime) => {
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    return (
      (currentHour > startHour ||
        (currentHour === startHour && currentMinute >= startMinute)) &&
      (currentHour < endHour ||
        (currentHour === endHour && currentMinute <= endMinute))
    );
  };

  const merchantTabs = useMemo(() => {
    const uniqueMap = new Map();

    merchantsData.forEach((item) => {
      const id = item?.merchant?._id;
      const name =
        item?.merchant?.name || item?.merchant?.username || 'Unknown Merchant';

      if (id && !uniqueMap.has(id)) {
        uniqueMap.set(id, { id, name });
      }
    });

    return [{ id: 'all', name: 'All' }, ...Array.from(uniqueMap.values())];
  }, [merchantsData]);

  const filteredMerchantsData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    let filtered = [...merchantsData];

    if (selectedMerchant !== 'all') {
      filtered = filtered.filter(({ merchant }) => merchant?._id === selectedMerchant);
    }

    filtered = filtered
      .map((item) => {
        const merchantName = (
          item?.merchant?.name ||
          item?.merchant?.username ||
          ''
        ).toLowerCase();

        let filteredFoods = [...(item.foods || [])];

        if (term) {
          filteredFoods = filteredFoods.filter((food) =>
            (food?.title || '').toLowerCase().includes(term)
          );

          if (merchantName.includes(term)) {
            filteredFoods = [...(item.foods || [])];
          }
        }

        return {
          ...item,
          foods: filteredFoods,
        };
      })
      .filter((item) => item.foods.length > 0);

    return filtered;
  }, [merchantsData, selectedMerchant, searchTerm]);

  return (
    <Container maxWidth="sm" className="food-wrapper">
      <div className="merchant-carousel">
        {merchantTabs.map((merchant) => (
          <button
            key={merchant.id}
            className={`merchant-chip ${
              selectedMerchant === merchant.id ? 'active' : ''
            }`}
            onClick={() => {
              setSelectedMerchant(merchant.id);
              localStorage.setItem('selectedMerchantFilter', merchant.id);
              window.dispatchEvent(
                new CustomEvent('merchantFilterChanged', {
                  detail: merchant.id,
                })
              );
            }}
          >
            {merchant.name}
          </button>
        ))}
      </div>

      {filteredMerchantsData.length === 0 ? (
        <div className="no-food-result">No foods or merchants found.</div>
      ) : (
        filteredMerchantsData.map(({ merchant, foods }) => {
          const merchantName = merchant?.name || merchant?.username || 'Unknown Merchant';

          return (
            <div key={merchant._id} className="merchant-section">
              {selectedMerchant === 'all' && (
                <Typography className="merchant-heading">
                  {merchantName}
                </Typography>
              )}

              <div className="food-list-vertical">
                {foods.map((food) => {
                  const isScheduled =
                    food.schedulingEnabled &&
                    food.schedule?.length > 0 &&
                    isCurrentDuration(
                      food.schedule[0].startTime,
                      food.schedule[0].endTime
                    );

                  const showAsAvailable =
                    !food.schedulingEnabled ||
                    food.schedule?.length === 0 ||
                    isScheduled;

                  const isDisabled = !showAsAvailable || !food.inStock;

                  return (
                    <div
                      key={food._id}
                      className={`food-row ${isDisabled ? 'disabled' : ''}`}
                    >
                      <div
                        className="food-thumb-wrap"
                        onClick={() => handleViewClick(food, merchant._id)}
                      >
                        <img
                          src={food.img}
                          alt={food.title}
                          className="food-thumb"
                        />
                      </div>

                      <div className="food-info">
                        <div
                          className="food-content"
                          onClick={() => handleViewClick(food, merchant._id)}
                        >
                          <h3 className="food-title">{food.title}</h3>

                          <p className="food-desc">
                            {food.description ||
                              'Fresh and delicious menu item prepared for your order.'}
                          </p>

                          <div className="food-meta">
                            <span>$$</span>
                            <span className="dot">•</span>
                            <span>{merchantName}</span>
                          </div>

                          {!food.inStock && (
                            <div className="stock-text">Out of Stock</div>
                          )}

                          {!showAsAvailable && (
                            <div className="stock-text">Not Available Now</div>
                          )}
                        </div>

                        <div className="food-bottom">
                          <button
                            className="price-add-btn"
                            disabled={isDisabled}
                            onClick={() => handleViewClick(food, merchant._id)}
                          >
                            <span className="btn-price">
                              RM {Number(food.price || 0).toFixed(2)}
                            </span>
                            <span className="btn-icon">
                              <AddIcon fontSize="small" />
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {selectedFood && (
        <FoodDetails food={selectedFood} onClose={handleCloseDetails} />
      )}
    </Container>
  );
};

export default connect(null, { addToCart })(FoodList);